import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { createChart, CandlestickSeries, LineSeries, createSeriesMarkers } from "lightweight-charts";
import { ArrowLeft, Trash2, Plus, BarChart3, Info } from "lucide-react";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";

/**
 * Interactive chart sandbox — practice plotting support/resistance lines
 * and identifying MA crossovers without financial risk.
 *
 * Synthetic random-walk OHLC data (no API calls — keeps the sandbox snappy
 * and fully offline). Users can:
 *   • Click anywhere on the chart to drop a horizontal support/resistance line
 *   • Drag the line to refine the level (via the price line drag)
 *   • Click an SR row in the side panel to remove it
 *   • Toggle MA(20) + MA(50) and visually spot crossovers
 *   • Reset the dataset with the "Reshuffle" button
 */

function generateSyntheticOHLC(seed = Date.now(), n = 180) {
  // Simple seeded RNG (Mulberry32) so "Reshuffle" produces a fresh-but-stable walk
  let s = seed >>> 0;
  const rand = () => {
    s = (s + 0x6D2B79F5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  const candles = [];
  let close = 100;
  const start = new Date();
  start.setDate(start.getDate() - n);
  let trend = 0;
  for (let i = 0; i < n; i++) {
    // Trend drifts and reverts so the chart has natural support/resistance bands
    trend += (rand() - 0.5) * 0.4;
    trend = Math.max(-2, Math.min(2, trend));
    const drift = trend * 0.3;
    const volatility = 1.2 + rand() * 1.0;
    const open = close;
    const change = drift + (rand() - 0.5) * volatility;
    close = Math.max(20, open + change);
    const high = Math.max(open, close) + rand() * volatility * 0.6;
    const low = Math.min(open, close) - rand() * volatility * 0.6;
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    candles.push({
      time: d.toISOString().slice(0, 10),
      open: +open.toFixed(2),
      high: +high.toFixed(2),
      low: +Math.max(20, low).toFixed(2),
      close: +close.toFixed(2),
    });
  }
  return candles;
}

function sma(data, period) {
  const out = [];
  for (let i = 0; i < data.length; i++) {
    if (i < period - 1) continue;
    let s = 0;
    for (let j = i - period + 1; j <= i; j++) s += data[j].close;
    out.push({ time: data[i].time, value: +(s / period).toFixed(2) });
  }
  return out;
}

function findCrossovers(short, long) {
  // Align by time, find points where short crosses above/below long
  const longMap = new Map(long.map((p) => [p.time, p.value]));
  const events = [];
  let prevDiff = null;
  for (const p of short) {
    const lv = longMap.get(p.time);
    if (lv == null) continue;
    const diff = p.value - lv;
    if (prevDiff != null && Math.sign(diff) !== 0 && Math.sign(diff) !== Math.sign(prevDiff)) {
      events.push({ time: p.time, value: p.value, kind: diff > 0 ? "golden" : "death" });
    }
    prevDiff = diff;
  }
  return events;
}

export default function ChartSandbox() {
  const ref = useRef(null);
  const chartRef = useRef(null);
  const candleRef = useRef(null);
  const ma20Ref = useRef(null);
  const ma50Ref = useRef(null);
  const markersRef = useRef(null);
  const linesRef = useRef([]); // {id, price, color}

  const [data, setData] = useState(() => generateSyntheticOHLC());
  const [showMA20, setShowMA20] = useState(true);
  const [showMA50, setShowMA50] = useState(true);
  const [showCrosses, setShowCrosses] = useState(true);
  const [srLines, setSrLines] = useState([]);
  const [hint, setHint] = useState("Tap the chart to drop a support/resistance line");

  // Mount chart
  useEffect(() => {
    if (!ref.current) return;
    const isDark = document.documentElement.classList.contains("dark");
    const palette = isDark
      ? { bg: "#1F2421", text: "#E8E6E2", grid: "#2C302E", border: "#2C302E" }
      : { bg: "#FFFFFF", text: "#2A2F2B", grid: "#EAE6E1", border: "#EAE6E1" };

    const chart = createChart(ref.current, {
      autoSize: true,
      localization: { locale: "en-US" },
      layout: {
        background: { color: palette.bg },
        textColor: palette.text,
        fontFamily: "IBM Plex Mono, monospace",
      },
      grid: {
        vertLines: { color: palette.grid },
        horzLines: { color: palette.grid },
      },
      rightPriceScale: { borderColor: palette.border },
      timeScale: { borderColor: palette.border, timeVisible: false },
      crosshair: { mode: 1 },
    });
    chartRef.current = chart;

    const candle = chart.addSeries(CandlestickSeries, {
      upColor: "#5C8D73",
      downColor: "#C66B59",
      borderUpColor: "#5C8D73",
      borderDownColor: "#C66B59",
      wickUpColor: "#5C8D73",
      wickDownColor: "#C66B59",
    });
    candle.setData(data);
    candleRef.current = candle;

    chart.subscribeClick((param) => {
      if (!param || !param.point || !candleRef.current) return;
      const price = candleRef.current.coordinateToPrice(param.point.y);
      if (price == null) return;
      const id = `sr-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      addSrLine({ id, price: +price.toFixed(2) });
    });

    return () => {
      chart.remove();
      chartRef.current = null;
      candleRef.current = null;
      ma20Ref.current = null;
      ma50Ref.current = null;
      markersRef.current = null;
      linesRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh data on reshuffle
  useEffect(() => {
    if (candleRef.current) candleRef.current.setData(data);
  }, [data]);

  // MA series toggle
  useEffect(() => {
    if (!chartRef.current) return;
    if (showMA20) {
      if (!ma20Ref.current) {
        ma20Ref.current = chartRef.current.addSeries(LineSeries, {
          color: "#547A8F",
          lineWidth: 2,
          priceLineVisible: false,
          lastValueVisible: false,
          title: "MA(20)",
        });
      }
      ma20Ref.current.setData(sma(data, 20));
    } else if (ma20Ref.current) {
      chartRef.current.removeSeries(ma20Ref.current);
      ma20Ref.current = null;
    }
  }, [showMA20, data]);

  useEffect(() => {
    if (!chartRef.current) return;
    if (showMA50) {
      if (!ma50Ref.current) {
        ma50Ref.current = chartRef.current.addSeries(LineSeries, {
          color: "#D18E63",
          lineWidth: 2,
          priceLineVisible: false,
          lastValueVisible: false,
          title: "MA(50)",
        });
      }
      ma50Ref.current.setData(sma(data, 50));
    } else if (ma50Ref.current) {
      chartRef.current.removeSeries(ma50Ref.current);
      ma50Ref.current = null;
    }
  }, [showMA50, data]);

  // Crossover markers (golden / death cross)
  useEffect(() => {
    if (!candleRef.current || !chartRef.current) return;
    if (!showCrosses || !showMA20 || !showMA50) {
      if (markersRef.current?.setMarkers) markersRef.current.setMarkers([]);
      return;
    }
    const crosses = findCrossovers(sma(data, 20), sma(data, 50));
    const markers = crosses.map((c) => ({
      time: c.time,
      position: c.kind === "golden" ? "belowBar" : "aboveBar",
      color: c.kind === "golden" ? "#5C8D73" : "#C66B59",
      shape: c.kind === "golden" ? "arrowUp" : "arrowDown",
      text: c.kind === "golden" ? "Golden" : "Death",
    }));
    if (!markersRef.current) {
      markersRef.current = createSeriesMarkers(candleRef.current, markers);
    } else {
      markersRef.current.setMarkers(markers);
    }
  }, [showCrosses, showMA20, showMA50, data]);

  const addSrLine = ({ id, price }) => {
    if (!candleRef.current) return;
    const color = "#6E94A8";
    const line = candleRef.current.createPriceLine({
      price,
      color,
      lineWidth: 2,
      lineStyle: 2, // dashed
      axisLabelVisible: true,
      title: `SR ${price.toFixed(2)}`,
    });
    linesRef.current.push({ id, price, line });
    setSrLines((prev) => [...prev, { id, price }]);
    setHint(`Support/resistance at $${price.toFixed(2)} — tap chip to remove`);
  };

  const removeSrLine = (id) => {
    const entry = linesRef.current.find((l) => l.id === id);
    if (entry && candleRef.current) {
      candleRef.current.removePriceLine(entry.line);
    }
    linesRef.current = linesRef.current.filter((l) => l.id !== id);
    setSrLines((prev) => prev.filter((s) => s.id !== id));
  };

  const clearAll = () => {
    if (candleRef.current) {
      for (const l of linesRef.current) candleRef.current.removePriceLine(l.line);
    }
    linesRef.current = [];
    setSrLines([]);
    setHint("All cleared. Tap the chart to drop a new line.");
  };

  const reshuffle = () => {
    clearAll();
    setData(generateSyntheticOHLC(Math.floor(Math.random() * 1e9)));
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] dark:bg-[#161A18]">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 py-10 md:py-14" data-testid="chart-sandbox-page">
        <Link to="/learn" className="inline-flex items-center gap-1.5 text-sm text-[#6B7068] hover:text-[#2A2F2B] dark:hover:text-[#E8E6E2] mb-6" data-testid="sandbox-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to Learn
        </Link>

        <header className="mb-6">
          <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#6B7068]">
            Practice
          </div>
          <h1 className="font-heading text-4xl tracking-tighter font-medium text-[#2A2F2B] mt-1">
            Chart sandbox
          </h1>
          <p className="text-[#6B7068] mt-2 max-w-xl">
            Click anywhere on the chart to drop a horizontal support/resistance line. Toggle moving averages to see how golden and death crosses look in real time. No money. No risk. Just practice.
          </p>
        </header>

        <div className="grid lg:grid-cols-[1fr_280px] gap-6">
          <div
            ref={ref}
            data-testid="sandbox-chart"
            className="rounded-2xl bg-white dark:bg-[#1F2421] border border-[#EAE6E1] dark:border-[#2C302E] h-[480px]"
          />

          <aside className="space-y-4">
            <section
              className="rounded-2xl bg-white dark:bg-[#1F2421] border border-[#EAE6E1] dark:border-[#2C302E] p-4"
              data-testid="sandbox-tools"
            >
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#6B7068] mb-3">Indicators</div>
              <label className="flex items-center justify-between text-sm text-[#2A2F2B] dark:text-[#E8E6E2] py-1">
                <span className="flex items-center gap-2">
                  <span className="w-3 h-1 rounded-full bg-[#547A8F]" /> MA (20)
                </span>
                <input
                  type="checkbox"
                  data-testid="sandbox-toggle-ma20"
                  checked={showMA20}
                  onChange={(e) => setShowMA20(e.target.checked)}
                />
              </label>
              <label className="flex items-center justify-between text-sm text-[#2A2F2B] dark:text-[#E8E6E2] py-1">
                <span className="flex items-center gap-2">
                  <span className="w-3 h-1 rounded-full bg-[#D18E63]" /> MA (50)
                </span>
                <input
                  type="checkbox"
                  data-testid="sandbox-toggle-ma50"
                  checked={showMA50}
                  onChange={(e) => setShowMA50(e.target.checked)}
                />
              </label>
              <label className="flex items-center justify-between text-sm text-[#2A2F2B] dark:text-[#E8E6E2] py-1">
                <span>Crossover markers</span>
                <input
                  type="checkbox"
                  data-testid="sandbox-toggle-crosses"
                  checked={showCrosses}
                  onChange={(e) => setShowCrosses(e.target.checked)}
                />
              </label>
            </section>

            <section
              className="rounded-2xl bg-white dark:bg-[#1F2421] border border-[#EAE6E1] dark:border-[#2C302E] p-4"
              data-testid="sandbox-sr-list"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#6B7068]">Your levels</div>
                {srLines.length > 0 && (
                  <button
                    onClick={clearAll}
                    data-testid="sandbox-clear-all"
                    className="text-xs text-[#C66B59] hover:underline"
                    aria-label="Clear all levels"
                  >
                    Clear
                  </button>
                )}
              </div>
              {srLines.length === 0 ? (
                <p className="text-xs text-[#6B7068] flex items-start gap-2">
                  <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  Tap the chart to add a line. They'll show up here.
                </p>
              ) : (
                <ul className="space-y-2">
                  {srLines.map((s) => (
                    <li
                      key={s.id}
                      className="flex items-center justify-between rounded-lg bg-[#F9F8F6] dark:bg-[#252A27] px-3 py-2"
                      data-testid="sandbox-sr-row"
                    >
                      <span className="font-mono-ticker text-sm text-[#2A2F2B] dark:text-[#E8E6E2]">
                        ${s.price.toFixed(2)}
                      </span>
                      <button
                        onClick={() => removeSrLine(s.id)}
                        data-testid={`sandbox-remove-${s.id}`}
                        className="text-[#6B7068] hover:text-[#C66B59]"
                        aria-label={`Remove level $${s.price.toFixed(2)}`}
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <Button
              onClick={reshuffle}
              data-testid="sandbox-reshuffle"
              variant="outline"
              className="w-full"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Reshuffle data
            </Button>

            <div className="rounded-2xl bg-[#D18E6315] dark:bg-[#DDA28415] border border-[#D18E63]/30 p-4 text-xs text-[#2A2F2B] dark:text-[#E8E6E2] flex items-start gap-2">
              <BarChart3 className="w-4 h-4 text-[#D18E63] mt-0.5 shrink-0" strokeWidth={1.5} />
              <span>{hint}</span>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}
